import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react'
import { useState } from 'react'
import { Badge, type BadgeColor } from 'app/components/ui/badge'
import { Button } from 'app/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'app/components/ui/table'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { formatCurrency } from 'lib/formatCurrency'
import type { Family, FamilyPerson } from 'app/types/related-contacts'
import { OPEN_LEAD_STATUSES, recipientFor } from 'lib/relatedContacts'

const STATUS_COLORS: Record<string, BadgeColor> = {
  'Needs Action': `amber`,
  'In Progress': `blue`,
  Scheduled: `green`,
}

function LeadStatus({ status }: { status?: string }) {
  if (!status) return <span className="text-minimal">Not a lead</span>
  return <Badge label={status === `Scheduled` ? `Booked` : status} color={STATUS_COLORS[status] ?? `gray`} />
}

function ReachThrough({ person, family }: { person: FamilyPerson; family: Family }) {
  const recipient = recipientFor(person, family)
  if (recipient.kind === `none`) return <Badge label={recipient.reason} color="red" />
  if (recipient.kind === `primary`)
    return <span className="text-subtle">via {recipient.person.name.split(` `)[0]}</span>
  return <span className="text-subtle">{formatPhoneNumber(person.phone) ?? `Direct`}</span>
}

function PersonCells({ person, family }: { person: FamilyPerson; family: Family }) {
  return (
    <>
      <TableCell>
        <LeadStatus status={person.leadStatus} />
      </TableCell>
      <TableCell>{person.nextAction ?? <span className="text-minimal">None</span>}</TableCell>
      <TableCell>
        <span className="text-strong tabular-nums">{formatCurrency(person.openTreatment)}</span>
      </TableCell>
      <TableCell>
        <ReachThrough person={person} family={family} />
      </TableCell>
    </>
  )
}

function FamilyRows({ family, onOpen }: { family: Family; onOpen: (person: FamilyPerson, family: Family) => void }) {
  const [expanded, setExpanded] = useState(true)
  const { primary, members } = family

  if (primary && members.length === 0) {
    return (
      <TableRow
        data-testid={`family-row-${family.id}`}
        className="cursor-pointer"
        onClick={() => onOpen(primary, family)}
      >
        <TableCell>
          <span className="font-medium text-strong">{primary.name}</span>
        </TableCell>
        <PersonCells person={primary} family={family} />
      </TableRow>
    )
  }

  const people = primary?.leadStatus ? [primary, ...members] : members
  const open = people.filter((p) => OPEN_LEAD_STATUSES.includes(p.leadStatus ?? ``))
  const total = people.reduce((sum, p) => sum + p.openTreatment, 0)
  const Caret = expanded ? CaretDownIcon : CaretRightIcon

  return (
    <>
      <TableRow
        data-testid={`family-row-${family.id}`}
        className="cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <TableCell>
          <div className="flex items-center gap-2">
            <Caret size={14} className="shrink-0 text-subtle" />
            {primary ? (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <Button
                  variant="link"
                  size="inline"
                  data-testid={`family-primary-${family.id}`}
                  onClick={(event) => {
                    event.stopPropagation()
                    onOpen(primary, family)
                  }}
                >
                  {primary.name}
                </Button>
                <Badge label="Primary contact" color="blue" />
                <span className="text-subtle">{primary.reason}</span>
              </div>
            ) : (
              <span className="font-semibold text-strong">{members[0]?.name.split(` `).at(-1)} family</span>
            )}
          </div>
        </TableCell>
        <TableCell>
          <span className="text-subtle">
            {open.length} of {people.length} open
          </span>
        </TableCell>
        <TableCell>{open.find((p) => p.nextAction)?.nextAction}</TableCell>
        <TableCell>
          <span className="font-semibold text-strong tabular-nums">{formatCurrency(total)}</span>
        </TableCell>
        <TableCell>
          {primary ? (
            <span className="text-subtle">{formatPhoneNumber(primary.phone)}</span>
          ) : (
            <Badge label="No adult to contact" color="red" />
          )}
        </TableCell>
      </TableRow>
      {expanded &&
        people.map((person) => (
          <TableRow
            key={person.id}
            data-testid={`family-member-row-${person.id}`}
            className="cursor-pointer"
            onClick={() => onOpen(person, family)}
          >
            <TableCell className="pl-10">
              <span className="text-strong">{person.name}</span>{' '}
              <span className="text-subtle">
                {[person.age !== undefined && person.age < 18 ? `${person.age}` : undefined, person.relationship]
                  .filter(Boolean)
                  .join(`, `)}
              </span>
            </TableCell>
            <PersonCells person={person} family={family} />
          </TableRow>
        ))}
    </>
  )
}

export function FamilyLeadsTable({
  families,
  onOpen,
}: {
  families: readonly Family[]
  onOpen: (person: FamilyPerson, family: Family) => void
}) {
  return (
    <Table className="min-w-190">
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Lead status</TableHead>
          <TableHead>Next action</TableHead>
          <TableHead>Open treatment</TableHead>
          <TableHead>Reach through</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {families.map((family) => (
          <FamilyRows key={family.id} family={family} onOpen={onOpen} />
        ))}
      </TableBody>
    </Table>
  )
}
